import { FC, useEffect, useRef } from 'react';
import { Redirect } from 'react-router';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { InnerhivePage } from '../InnerhivePage';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { CareMapState } from '../caremap/CareMapState';
import { useTrackOnVisible } from '../metrics/useTrackOnVisible';
import { WIDE_CONTENT } from '../components/wideContent';
import { OrganizationsState } from './OrganizationsState';
import { OrganizationInsights } from './OrganizationInsights';
import { OrganizationClients } from './OrganizationClients';
import { OrganizationTeam } from './OrganizationTeam';
import { AgencyTabsProvider } from './AgencyTabsProvider';
import { AGENCY_TAB_PARAM, agencyTabPath, IAgencyTab, isAgencyTab } from './agencyTabs';

const useStyles = createUseStyles({
    content: {
        position: 'relative',
        width: '100%',
        maxWidth: WIDE_CONTENT.maxWidth,
        margin: '0 auto',
        padding: `16px ${WIDE_CONTENT.sidePadding}px 30px`
    }
});

/** Mounted afresh for each tab, as every tabbed page is, which is how the header learns which tab is showing. */
const AgencyHomeTab: FC<{ orgId: string; orgName: string; tab: IAgencyTab }> = ({ orgId, orgName, tab }) => {
    const classes = useStyles();
    const tabsProvider = useInstance(AgencyTabsProvider);

    // No Bea button: the header's Bea belongs to a single care recipient, and this page is not looking at one.
    return (
        <InnerhivePage title={orgName} tabs={tabsProvider.getTabs()} fullWidth hidesBeaButton backgroundColor="light">
            <div className={classes.content}>
                {tab === 'insights' && <OrganizationInsights orgId={orgId} />}
                {tab === 'clients' && <OrganizationClients orgId={orgId} />}
                {tab === 'team' && <OrganizationTeam orgId={orgId} />}
            </div>
        </InnerhivePage>
    );
};

/**
 * Moving to a tab counts, by its button or by Back, and so does landing on a tab a caller names;
 * landing anywhere else does not. A header tab's onClick cannot carry this, because the tab's link
 * stops the click before it reaches it.
 */
const useTrackAgencyTabOpened = (params: { tab?: IAgencyTab; namedTab: string | null; landingTab: IAgencyTab }) => {
    const { tab, namedTab, landingTab } = params;
    const tabsProvider = useInstance(AgencyTabsProvider);

    const shownTab = useRef<IAgencyTab | undefined>(undefined);
    useEffect(() => {
        if (!tab) {
            if (isAgencyTab(namedTab)) {
                tabsProvider.trackOpened(namedTab);
            }
            shownTab.current = landingTab;
            return;
        }

        if (shownTab.current && shownTab.current !== tab) {
            tabsProvider.trackOpened(tab);
        }
        shownTab.current = tab;
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Home for an agency's staff: its caseload, under tabs. Without a tab it is the landing, which goes
 * to the tab a caller names, else the one they were last on, and keeps the rest of the query string
 * so the client list's ?user= filter still applies. One page serves both, so landing again (a second
 * tap on Home) does not count another visit.
 */
export const AgencyHomePage: FC<{ tab?: IAgencyTab; search: string; careMapId?: string }> = ({ tab, search, careMapId }) => {
    const orgsState = useInstance(OrganizationsState);
    useSubscription(() => orgsState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const tabsProvider = useInstance(AgencyTabsProvider);

    // The arrival the consumer home counts too, so the home figure keeps counting agency staff.
    useTrackOnVisible(`Caregiver's Corner Opened`);

    const landingParams = new URLSearchParams(search);
    const namedTab = landingParams.get(AGENCY_TAB_PARAM);
    landingParams.delete(AGENCY_TAB_PARAM);
    const landingTab = isAgencyTab(namedTab) ? namedTab : tabsProvider.getLandingTab();

    useTrackAgencyTabOpened({ tab, namedTab, landingTab });

    // The router sends only an agency's staff here, and they always have an organization.
    const org = orgsState.getMostRecentOrganization();
    if (!org) {
        return null;
    }

    if (!tab) {
        return <Redirect to={{ pathname: agencyTabPath(landingTab), search: landingParams.toString() }} />;
    }

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId || careMapState.getMostRecentCaremap()?.careMapId}>
            <AgencyHomeTab key={tab} orgId={org.orgId} orgName={org.name} tab={tab} />
        </ActiveCareMapProviderV2>
    );
};
